import { injectable } from "inversify";
import { AnalyticsEvent, BaseAnalyticsEvent } from "./AnalyticEvent";
import { AnalyticsSender } from "./AnalyticsSender";
import { getClientHints } from "../../utils/ClientHints";

export interface VintomAnalyticsSender extends AnalyticsSender {
  initialize(vintomEndpoints: string[]): void;
}

@injectable()
export class VintomAnalyticsSenderImpl implements VintomAnalyticsSender {
  private endpoints: string[] = [];

  initialize(vintomEndpoints: string[]): void {
    this.endpoints = vintomEndpoints;
  }

  sendEvent(event: AnalyticsEvent): void {
    const eventJSON = this.serializeEventToJSON(event);
    if (eventJSON) {
      for (const endpoint of this.endpoints) {
        fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
          body: JSON.stringify(eventJSON),
        });
      }
    }
  }

  private serializeEventToJSON(event: AnalyticsEvent): any {
    const base = this.serializeBaseAnalyticsEvent(event);

    switch (event.type) {
      case "PLAYER_INIT":
        return base;
      case "PLAYER_START":
        return base;
      case "PLAYER_ERROR":
        return {
          ...base,
          ei: event.errorCode,
          em: event.errorMessage,
        };
      case "SCENARIO_ELEMENT_START":
        return {
          ...base,
          st: event.videoType,
          t: event.currentTime,
          vo: event.volume * 100,
          d: event.duration * 1000,
        };
      case "SCENARIO_ELEMENT_PLAYING":
        return {
          ...base,
          st: event.videoType,
          t: event.currentTime * 1000,
          vo: event.volume * 100,
          d: event.duration * 1000,
          ts: event.startTime * 1000,
          er: event.endReason,
        };
      case "CTA_SHOW":
        return {
          ...base,
          ct: event.ctaId,
        };
      case "CTA_ACTION":
        return {
          ...base,
          ct: event.ctaId,
          ac: event.actionId,
          ds: event.actionAdditionalData,
        };
      case "NOT_SUPPORTED_BROWSER":
        return base;
      default:
        // all other events are ignored by VintomAnalyticsSender
        return undefined;
    }
  }

  private serializeBaseAnalyticsEvent<T extends string>(event: BaseAnalyticsEvent<T>): any {
    return {
      type: event.type,
      dttm: event.timestamp,
      se: event.scenarioElementId,
      c: event.videoCode,
      vui: event.userIdentifier,
      vd: event.videoDate,
      pc: event.projectCode,
      gr: event.groupId,
      pv: getClientHints().platformVersion,
    };
  }
}
